/**
 * features/leads/leadsWrite.ts
 * Public submission goes through the submitLead callable (no auth).
 * Operator actions update status, notes, convert to a client, or delete.
 */
import { doc, deleteDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, updateDoc, functions } from '@/lib/firebase';
import { createClient } from '@/features/clients/clientsWrite';
import { COLLECTIONS, type Lead, type LeadStatus, type ClientContact } from '@/types';

export interface LeadSubmission {
  name: string;
  email: string;
  phone?: string;
  company?: string;
  message?: string;
  hp?: string; // honeypot
}

const submitLeadFn = httpsCallable<LeadSubmission, { ok: boolean; id: string | null }>(functions, 'submitLead');

/** Public enquiry submission (used by the website form). */
export async function submitLead(data: LeadSubmission): Promise<void> {
  await submitLeadFn(data);
}

export async function setLeadStatus(id: string, status: LeadStatus): Promise<void> {
  await updateDoc(doc(db, COLLECTIONS.leads, id), { status, updatedAt: new Date().toISOString() });
}

export async function updateLeadNotes(id: string, notes: string): Promise<void> {
  await updateDoc(doc(db, COLLECTIONS.leads, id), { notes: notes.trim(), updatedAt: new Date().toISOString() });
}

export async function deleteLead(id: string): Promise<void> {
  await deleteDoc(doc(db, COLLECTIONS.leads, id));
}

/** Convert a lead into a client and mark it won. */
export async function convertToClient(lead: Lead, actor: string): Promise<string> {
  const contact: ClientContact = { name: lead.name, primary: true };
  if (lead.email) contact.email = lead.email;
  if (lead.phone) contact.phone = lead.phone;

  const clientId = await createClient(
    {
      name: lead.company?.trim() || lead.name,
      type: lead.company ? 'company' : 'individual',
      contacts: [contact],
      active: true,
      ...(lead.email ? { email: lead.email } : {}),
      ...(lead.phone ? { phone: lead.phone } : {}),
      ...(lead.message ? { notes: lead.message } : {}),
    },
    actor,
  );

  await updateDoc(doc(db, COLLECTIONS.leads, lead.id), {
    status: 'won',
    convertedClientId: clientId,
    updatedAt: new Date().toISOString(),
  });
  return clientId;
}
